import { Heading, Text } from '@ui'
import { APP_NAME } from '../brand.js'

// Public (no sign-in): the app's Settings links here, and a Play Store listing will need it.
// Minimal for now (decided 2026-10-03); extend it before anyone else uses the app or site, and
// whenever a new service starts receiving data (Sentry, Crashlytics, app sync).
export default function Privacy() {
  return (
    <article className="legal">
      <Heading size={3}>Privacy</Heading>
      <Text as="p" muted>Last updated 11 October 2026.</Text>

      <Heading level={2} size={5} mt={4}>The app</Heading>
      <p>
        {APP_NAME} records your walks on your phone: GPS positions, step count and air pressure
        (for height). Unless you sign in, they stay on the phone. The one exception is a temporary
        backup to the developer's own Dropbox, for the developer's phones only; it will be removed
        before the app is shared.
      </p>
      <p>
        You can sign in to the website from the app's Settings, with Google through Firebase
        Authentication (the same sign-in as the website). Signed in, the app sends each finished
        walk to the website straight after you save it, and any you choose to send: its name,
        notes, times, distance, steps, climb, the phone model it was recorded on, and every GPS
        position recorded. Then the walk's air pressure readings (for height) and the weather
        forecasts and observations stored with it follow, and the photos and videos taken from
        the app during the walk, with the position and time each was taken (a video in a format
        browsers can't play is sent as a converted copy). Once sent, a walk's
        name and notes are edited on the website. Deleting a walk or photo on the phone doesn't
        delete it from the website. Sign out in Settings to stop sending.
      </p>
      <p>
        When the app crashes, a crash report goes to Google's{' '}
        <a href="https://firebase.google.com/docs/crashlytics" target="_blank" rel="noreferrer">Firebase Crashlytics</a>:
        what failed (the stack trace), the phone's model and Android version, the app's version, and
        a random ID for the app on that phone. Reports don't include walks, locations, photos or
        anything about your account.
      </p>
      <p>To show maps and weather, the app sends some data to these services:</p>
      <ul>
        <li>Google Maps and Google's Weather API: the map area you view, and your position for the weather.</li>
        <li>Ordnance Survey: the map tiles you view, when an OS map style is chosen.</li>
        <li>Met Office, MET Norway and Open-Meteo: a walk's start position, for its forecast and observations.</li>
      </ul>
      <p>Each service handles that data under its own privacy policy.</p>

      <Heading level={2} size={5} mt={4}>This website</Heading>
      <p>
        The website is private: only invited Google accounts can sign in. Sign-in uses Google
        through Firebase Authentication. After sign-in the site keeps one cookie, which holds your
        email address and lasts 30 days, and your browser remembers display choices (such as miles
        or kilometres). The walks shown are the account owner's own. The site is hosted by Fly.io in
        London; its map tiles come from Ordnance Survey through the site's own server, or, when the
        Mapbox Outdoors style is chosen, straight from Mapbox, which then sees the map area viewed and
        your IP address. Walk photos
        and videos are kept in Tigris storage (through Fly.io) and are only shown to signed-in
        users, through the site's own server. Photos and videos added on the site are stored as
        they are, including any time and location the phone saved in them. Deleting one hides it
        on the site; the file itself is kept. For a walk recorded on a phone with a barometer, the
        site asks <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">Open-Meteo</a>{' '}
        for that day's air pressure near the walk's start (rounded to about a kilometre), when no
        Met Office observations came with the walk, to work out its height.
      </p>
      <p>
        The account owner can share a walk with a link. Anyone who has the link can see that walk,
        read-only, until the link expires or is stopped; the owner sees how many times it was
        opened, but not by whom. With personal details hidden (the default), the shared page
        leaves out the first and last 300 metres of the route, the notes, the times of day, the
        phone used and any photos or videos taken in the hidden parts. A link shows photos unless
        the owner turns them off, as resized copies that carry no location or camera details, and
        videos only if the owner includes them, without their recording time or phone details
        but with their sound as recorded. Shared pages load
        their map from OpenStreetMap, which then sees the visitor's IP address. Shared pages ask
        search engines not to list them. When a link is pasted into a chat or social app, that app
        fetches a preview card: the walk's name (or its date), distance, time and climb, and a
        drawing of the route without a map, trimmed the same way when personal details are hidden.
        An app may keep that card after the link expires or is stopped.
      </p>
      <p>
        The owner can also share the photos from the walks of a range of days with a link, on the
        same terms. With personal details hidden (the default), photos and videos taken within 300
        metres of their walk's start or finish are left out, along with any that can't be placed
        to check, and the page shows only each walk's day, never the time; the map places each
        photo where it was taken. The preview card for such a link shows a few of its photos.
      </p>
      <p>
        When something goes wrong on the site, an error report goes to{' '}
        <a href="https://sentry.io/" target="_blank" rel="noreferrer">Sentry</a>: what failed, the
        page's address, and the browser and system it ran on. Reports don't include your email
        address, IP address or cookies.
      </p>
      <p>No data is sold or used for advertising, and there is no tracking or analytics.</p>
    </article>
  )
}
